import * as JsSIP from "jssip";
import {v4 as uuidv4} from 'uuid';
import {RTCSession} from "jssip/lib/RTCSession";
import { DTMF_TRANSPORT } from 'jssip/lib/Constants';
import ringtoneSound from '../../../assets/sounds/ring.mp3';
import {
    setCallFrom,
    setCallState,
    setIncomingCall,
    setSipInstanceId,
    setCallTo,
    setIvrKeyName,

    navigateDialer, setDialerHidden, setSpam, playRingback, stopRingback, stopRingtone,
    engineCallOf, tagEngine
} from "../../../store/features/calls/dialerSlice";
import {CallStateEnum} from "../callState";
import {buildAudioConstraints} from "../hooks/audioConstraints";
import logo from '../../../assets/logo.svg';
import {AppDispatch, store} from "../../../store/store";
import logger from "../../../services/logger";
import {trackSipCall} from "../../../utils/loggerHelpers";
import busySound from  "../../../assets/sounds/busy.mp3"
import webrtcCompletedSound from '../../../assets/sounds/webrtc_completed.mp3';
import webrtcDisconnectedSound from '../../../assets/sounds/webrtc_disconnected.mp3';
import connectingSound from '../../../assets/sounds/connecting.mp3';
import {useMemo} from "react";
import {RingbackManager} from "../../../services/callService.ts";
import { getIceServers, getIceGatheringTimeout, prefetchIceServers, hasCachedTurnServers, clearIceServerCache } from "../../../utilis/iceServers";
import { isForcedRelay } from "../../../utilis/iceTransportPolicy";
import { stripMdnsCandidates, countMdnsCandidates } from "../../../utilis/sdpUtils";
import { setupICECandidateHandling, type SessionTimings } from "../../../utilis/iceCandidateHandler";
import {createAudioHealthMonitor} from "../../../utilis/audioHealthMonitor.ts";
import {setupNetworkChangeHandling} from "../../../utilis/networkChangeHandler";
import {getVariant, getAudioFlags, type AudioFeatureFlags} from "../../../services/abTest/variant";
import {attachWorkletPath, detachWorkletPath, needsRemoteStreamAnchor} from "../../../utilis/backgroundedAudioWorklet";
import {startAudioDiagTick, stopAudioDiagTick} from "../../../utilis/audioDiagTick";
import {startAudioCallLogger, stopAudioCallLogger, loggedPlay} from "../../../utilis/audioCallLogger";
import {describeLocalAudioTrack} from "../../../utilis/localTrackSettings";
import {getAccessToken, refreshAccessToken, SessionEndedError, subscribeToAccessTokenChanges} from "../../../services/authRuntime";
import {getAccountBlocked} from "../../../services/accountAccessState";
import {isSipBusy, shouldRejectIncomingSipAsBusy, SIP_BUSY_HERE} from "./sipBusyPolicy";
// Side-effect import — arms the page-level diagnostic listeners once at module load.
import "../../../utilis/audioPageLogger";
import { focusAppWindow } from '../../../utils/desktopBridge';

/**
 * Inject OPUS tuning params into an SDP fmtp line.
 *
 * Tells the far side to encode Opus with FEC, DTX, mono, VBR, and a 32 kbps
 * ceiling. The ceiling gives ~12 kbps of headroom above a bare voice stream
 * for FEC redundancy on lossy uplinks (WiFi APs under contention in
 * particular). DTX suppresses packet transmission during silence, letting the
 * receiver substitute RFC 3389 CN instead of low-bitrate comfort packets.
 *
 * Each param is idempotent: re-running on an already-patched SDP is a no-op.
 * The word-boundary check avoids false positives between `stereo=` and
 * `sprop-stereo=` (both contain the substring `stereo=`).
 */
/**
 * Return the Opus fmtp params to inject based on the active variant flags.
 * - Variant A: no tuning (empty array → injectOpusParams is a no-op).
 * - Variant B: DTX only (PR1516 behaviour).
 * - Variant C: full PR1566 tuning block.
 */
function getOpusFmtpParams(flags: AudioFeatureFlags): ReadonlyArray<readonly [string, string]> {
    if (flags.opusFullTuning) {
        // 48 kbps on variant C (opusHdBitrate): matches Zoom/Discord/FaceTime HD voice
        // target, stays inside Opus wideband, and leaves ~28 kbps headroom above a
        // bare voice stream for FEC redundancy. WebRTC-to-WebRTC P2P calls bypass
        // rtpengine here and carry Opus end-to-end, so this is the real wire ceiling.
        // 32 kbps stays the ceiling on variant B for contended-WiFi headroom.
        const maxBitrate = flags.opusHdBitrate ? '48000' : '32000';
        return [
            ['useinbandfec', '1'],
            ['usedtx', '1'],
            ['stereo', '0'],
            ['sprop-stereo', '0'],
            ['cbr', '0'],
            ['maxaveragebitrate', maxBitrate],
        ];
    }
    if (flags.opusDtx) {
        return [['usedtx', '1']];
    }
    return [];
}

function injectOpusParams(sdp: string): string {
    const params = getOpusFmtpParams(getAudioFlags());
    if (params.length === 0) return sdp;
    const opusPtMatch = sdp.match(/a=rtpmap:(\d+) opus\/48000\/2/i);
    if (!opusPtMatch) return sdp;
    const opusPt = opusPtMatch[1];
    const fmtpRegex = new RegExp(`(a=fmtp:${opusPt} [^\\r\\n]*)`, 'i');
    const hasParam = (s: string, key: string) =>
        new RegExp(`(?:[;\\s])${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=`).test(s);
    if (fmtpRegex.test(sdp)) {
        return sdp.replace(fmtpRegex, (match: string) => {
            let result = match;
            for (const [key, value] of params) {
                if (!hasParam(result, key)) {
                    result += `;${key}=${value}`;
                }
            }
            return result;
        });
    }
    // No fmtp line — insert one after the rtpmap line with the full param block
    const paramString = params.map(([k, v]) => `${k}=${v}`).join(';');
    return sdp.replace(
        new RegExp(`(a=rtpmap:${opusPt} opus\\/48000\\/2[^\\r\\n]*)`, 'i'),
        (match: string) => `${match}\r\na=fmtp:${opusPt} ${paramString}`
    );
}

/**
 * Advertise RTCP NACK on the Opus payload type.
 *
 * WebRTC only emits `a=rtcp-fb:<pt> nack` for video by default; audio has to
 * be munged in. When the remote peer honours it, single-packet loss is
 * recovered via retransmit instead of NetEQ concealment — audibly smoother
 * on lossy WiFi / contended uplinks.
 *
 * SDP offer/answer rules: a feedback capability can only appear in the answer
 * if it was present in the offer. Offer path always injects; answer path only
 * injects when the remote offer advertised nack on the same PT (explicit) or
 * the wildcard `a=rtcp-fb:* nack` form (RFC 4585 §4.2). Otherwise strict
 * peers can reject setRemoteDescription.
 *
 * Idempotent: re-running on an already-patched SDP is a no-op.
 */
/**
 * Attach the SDP-mangling listener to a JsSIP session.
 *
 * JsSIP 3.x does NOT honour the `sdpTransform` option that was being passed
 * to `ua.call()` / `session.answer()` in this codebase — it's silently
 * ignored, which meant all the Opus DTX/FMTP/NACK munging configured via
 * that option never reached the wire. The supported hook is the `sdp`
 * event: JsSIP emits `{originator, type, sdp}` before setLocalDescription
 * and uses the (mutated) `e.sdp` as the final SDP for the offer/answer.
 *
 * This function registers on the `sdp` event, filters to local originator
 * only (we don't mangle remote SDPs), adds `a=maxptime:20` for low-latency
 * Opus packetisation, runs `injectOpusParams` (FEC/DTX/FMTP per variant
 * flags) and `injectOpusNack` (RTCP NACK for retransmit). Answer path only
 * advertises NACK when the remote offer did — looked up from the session's
 * remoteDescription which JsSIP sets before the local answer is generated.
 */
function installSdpMangling(session: RTCSession, direction: 'incoming' | 'outgoing'): void {
    (session as any).on('sdp', (e: any) => {
        if (e.originator !== 'local') return;
        const original: string = e.sdp ?? '';
        let sdp = original;
        if (!/a=maxptime:20/.test(sdp)) {
            sdp = sdp.replace(/a=rtpmap:\d+ opus\/48000\/2\r\n/, (match: string) => match + "a=maxptime:20\r\n");
        }
        // Codex audio-quality pass — pair `a=ptime:20` with the existing
        // maxptime:20. 20 ms is the VoIP sweet spot for Opus (RFC 6716):
        // longer frames save header overhead but degrade glitch recovery
        // under burst loss/jitter. maxptime alone caps the upper bound;
        // ptime nudges the actual cadence to the recommended value.
        if (!/a=ptime:20/.test(sdp)) {
            sdp = sdp.replace(/a=rtpmap:\d+ opus\/48000\/2\r\n/, (match: string) => match + "a=ptime:20\r\n");
        }
        // BBB pattern #1 — strip mDNS (.local) host candidates from the
        // outgoing local SDP. JsSIP 3.10.1 fires this 'sdp' event AFTER
        // setLocalDescription completes AND ICE gathering is ready (see
        // RTCSession.js lines 1942 & 1976: `sdp: connection.localDescription.sdp`).
        // So the SDP at this point DOES contain a=candidate lines and we
        // can strip the mDNS-hostname ones before they leave the browser.
        // Re-INVITE / ICE-restart paths use a different code path that
        // emits 'sdp' pre-gathering (lines 2337+) — there the strip is a
        // no-op, which is fine; those SDPs don't have candidates yet.
        //
        // A `stripped: 0` value in logs would mean we're on a pre-gathering
        // emit path — investigators can query for stripped > 0 to confirm
        // the mitigation is actually engaging in production.
        const candidateCountBefore = (sdp.match(/^a=candidate:/gm) ?? []).length;
        const mdnsCount = countMdnsCandidates(sdp);
        if (mdnsCount > 0) {
            sdp = stripMdnsCandidates(sdp);
        }
        if (candidateCountBefore > 0 || mdnsCount > 0) {
            logger.info('SDP candidates processed', {
                source: 'sip',
                event: 'sdp_mdns_stripped',
                direction,
                type: e.type,
                candidatesInSdp: candidateCountBefore,
                stripped: mdnsCount,
            });
        }
        sdp = injectOpusParams(sdp);
        let mangled: string;
        if (e.type === 'offer') {
            mangled = injectOpusNack(sdp);
            logger.info('OPUS params injected into offer SDP', {
                source: 'sip',
                event: 'opus_params_inject_offer',
                direction,
                injected: mangled !== original,
            });
        } else {
            const remoteOfferSdp = (session.connection as any)?.remoteDescription?.sdp ?? '';
            mangled = injectOpusNack(sdp, remoteOfferSdp);
            logger.info('OPUS params injected into answer SDP', {
                source: 'sip',
                event: 'opus_params_inject_answer',
                direction,
                injected: mangled !== original,
            });
        }
        e.sdp = mangled;
    });
}

function injectOpusNack(sdp: string, remoteOfferSdp?: string): string {
    if (!getAudioFlags().opusNack) return sdp;
    const opusPtMatch = sdp.match(/a=rtpmap:(\d+) opus\/48000\/2/i);
    if (!opusPtMatch) return sdp;
    const opusPt = opusPtMatch[1];
    // Match BARE generic NACK only: `a=rtcp-fb:<pt> nack<EOL>`. Parameterised
    // variants (`nack pli`, `nack sli`, `nack rpsi`) are different feedback
    // modes and don't authorise us to advertise plain NACK on the answer.
    // SDP is line-oriented (CRLF / LF), so the terminator is end-of-line or
    // end-of-string.
    const BARE_NACK_TAIL = '(?=\\r|\\n|$)';
    if (remoteOfferSdp !== undefined) {
        const remoteHasNack =
            new RegExp(`a=rtcp-fb:${opusPt} nack${BARE_NACK_TAIL}`, 'i').test(remoteOfferSdp) ||
            new RegExp(`a=rtcp-fb:\\* nack${BARE_NACK_TAIL}`, 'i').test(remoteOfferSdp);
        if (!remoteHasNack) return sdp;
    }
    const nackRegex = new RegExp(`a=rtcp-fb:${opusPt} nack${BARE_NACK_TAIL}`, 'i');
    if (nackRegex.test(sdp)) return sdp;
    // Insert directly after the existing fmtp line if present, else after rtpmap.
    const fmtpRegex = new RegExp(`(a=fmtp:${opusPt} [^\\r\\n]*)`, 'i');
    if (fmtpRegex.test(sdp)) {
        return sdp.replace(fmtpRegex, (match: string) => `${match}\r\na=rtcp-fb:${opusPt} nack`);
    }
    return sdp.replace(
        new RegExp(`(a=rtpmap:${opusPt} opus\\/48000\\/2[^\\r\\n]*)`, 'i'),
        (match: string) => `${match}\r\na=rtcp-fb:${opusPt} nack`,
    );
}

export let sipUserAgent: JsSIP.UA | null = null;
type MediaAccessError = { name?: string; message?: string; constraint?: string } | undefined;

let session: RTCSession | null = null;

export interface SipControls {
    readonly muted: boolean;
    readonly held: boolean;
    readonly direction?: 'incoming' | 'outgoing';
}

const IDLE_CONTROLS: SipControls = {muted: false, held: false};
let sipControls: SipControls = IDLE_CONTROLS;
const sipControlListeners = new Set<() => void>();

const publishSipControls = (): void => {
    const next: SipControls = session
        ? {muted: !!session.isMuted().audio, held: !!session.isOnHold().local, direction: session.direction === 'incoming' ? 'incoming' : 'outgoing'}
        : IDLE_CONTROLS;
    if (next.muted === sipControls.muted && next.held === sipControls.held && next.direction === sipControls.direction) return;
    sipControls = next;
    sipControlListeners.forEach(listener => listener());
};

export const subscribeSipControls = (listener: () => void): (() => void) => {
    sipControlListeners.add(listener);
    return () => {
        sipControlListeners.delete(listener);
    };
};

export const getSipControlsSnapshot = (): SipControls => sipControls;
let ongoingCalls = 0;

const isGroupCallActive = (): boolean => store.getState().dialer.callState === CallStateEnum.GROUP_CALL;

const rejectIncomingAsBusy = (incoming: RTCSession | undefined): void => {
    if (isGroupCallActive()) {
        logger.info('Incoming SIP call rejected as busy during a group call', {
            source: 'sip',
            event: 'incoming_rejected_group_call',
            callId: incoming?.id,
            statusCode: SIP_BUSY_HERE.status_code,
        });
    } else {
        logger.info('Incoming SIP call rejected as busy during another SIP call', {
            source: 'sip',
            event: 'incoming_rejected_sip_call',
            callId: incoming?.id,
            statusCode: SIP_BUSY_HERE.status_code,
        });
    }
    try {
        incoming?.terminate({...SIP_BUSY_HERE});
    } catch (error) {
        logger.warn('Failed to reject incoming SIP call as busy', {
            source: 'sip',
            event: 'incoming_reject_busy_failed',
            callId: incoming?.id,
            errorMessage: (error as Error)?.message,
        });
    }
};

/**
 * Mark a specific RTCSession as torn down so late-arriving async callbacks
 * (networkPriority retry, jitter-target apply) can no-op on that exact
 * session. Per-session (not module-global) so a late 'ended'/'failed' from
 * the PREVIOUS call cannot suppress setup on a subsequent back-to-back call.
 */
const markSessionEnded = (s: RTCSession | null | undefined): void => {
    if (s) (s as any).__callEnded = true;
};
/**
 * True when this specific session has been torn down — either by user-
 * initiated endCall/rejectCall (explicit `__callEnded` marker) or by JsSIP's
 * natural state machine reaching CANCELED (7) / TERMINATED (8). The status
 * check means we don't have to re-mark the session inside every 'ended' /
 * 'failed' event handler.
 *
 * JsSIP _status reference (from answerCall() duplicate-check):
 *   0=NULL, 1=INVITE_SENT, 2=1XX_RECEIVED, 3=INVITE_RECEIVED,
 *   4=WAITING_FOR_ANSWER, 5=ANSWERED, 6=WAITING_FOR_ACK,
 *   7=CANCELED, 8=TERMINATED, 9=CONFIRMED.
 * Only 7 and 8 are terminal. 9 (CONFIRMED) is a LIVE call — matching
 * `>= 7` would silence the post-confirmed audio tuning we're gating.
 */
const isSessionEnded = (s: RTCSession | null | undefined): boolean => {
    if (!s) return true;
    if ((s as any).__callEnded) return true;
    const status = (s as any)._status;
    return status === 7 || status === 8;
};

/**
 * Stable per-call identifier for log correlation and the diagnostic
 * ownership-keyed lifecycle (audioCallLogger.persistedState,
 * audioDiagTick.persistedChopState).
 *
 * JsSIP exposes `session.id` early in most flows, but there are paths
 * where it's missing (very early failed events, transport errors before
 * SRD). The previous code returned the literal `'unknown'` in those
 * cases, which let two unrelated calls share the same callId — causing
 * the diagnostic state to bleed across calls until guards were added.
 *
 * This helper returns `session.id` when present; otherwise synthesizes
 * a stable id and stashes it on the session so subsequent reads return
 * the same value. UUID over Date.now()+random because uuid is already
 * imported and gives us guaranteed uniqueness across rapid call boundaries.
 */
const getCallId = (s: RTCSession | null | undefined): string => {
    if (!s) return `nosession-${uuidv4().slice(0, 8)}`;
    const native = (s as unknown as {id?: string}).id;
    if (typeof native === 'string' && native.length > 0) return native;
    const stash = s as unknown as {__phqSyntheticCallId?: string};
    if (stash.__phqSyntheticCallId) return stash.__phqSyntheticCallId;
    const synthetic = `synthetic-${uuidv4().slice(0, 8)}`;
    stash.__phqSyntheticCallId = synthetic;
    return synthetic;
};

/**
 * Set to true when the user hangs up (endCall / rejectCall) while call() is
 * still awaiting getIceServers(). Checked after the await — if true, abort
 * before sipUserAgent.call() to prevent a ghost INVITE from firing after the
 * UI already cleaned up.
 */
let _callAborted = false;

/**
 * True while call() is executing (from entry to sipUserAgent.call()).
 * Guards against double-invocation — a second call() while the first is still
 * awaiting getIceServers() would fire two INVITEs, and the second session
 * overwrites the module-level `session` var. When the second INVITE fails
 * (NORMAL_CIRCUIT_CONGESTION), the failed handler resets UI to IDLE while
 * the first call is still alive and unreachable — endCall() can't terminate it.
 */
let _callInProgress = false;

/** Cleanup function for the active network change handler (one per call). */
let networkChangeCleanup: (() => void) | null = null;

/** Cleanup function for the tab visibility handler (one per call). */
let visibilityCleanup: (() => void) | null = null;

/**
 * Register a visibilitychange listener that runs an immediate audio health
 * check when the user returns to the PhoneHQ tab during an active call.
 *
 * Chrome aggressively throttles background tabs (timers capped to 1/min,
 * WebRTC processing deprioritised). When the user switches back, the RTP
 * pipeline may need a moment to recover. Running the health monitor poll
 * immediately surfaces any stale state and lets the banner update.
 */
function setupTabVisibilityHandling(activeSession: RTCSession, callId: string): () => void {
    const handler = () => {
        if (document.hidden || !activeSession?.isEstablished?.()) return;
        const monitor = (activeSession as any).audioMonitor;
        if (monitor?.runNow) {
            logger.info('Tab became visible during call — running immediate audio check', {
                source: 'sip', event: 'tab_visible_audio_check', callId,
            });
            monitor.runNow();
        }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
}

/** Debounce flag for softReconnectTransport to prevent concurrent reconnects. */
let isSoftReconnecting = false;

/**
 * Soft reconnect -- disconnect and reconnect WebSocket without stopping UA.
 * Preserves active calls while fixing WebSocket connectivity.
 * Ported from mobile UAManager.softReconnectTransport().
 */
const softReconnectTransport = (): void => {
    if (!sipUserAgent) {
        logger.warn('Cannot soft reconnect: UA not initialized', {
            source: 'sip', event: 'soft_reconnect_no_ua',
        });
        return;
    }

    if (isSoftReconnecting) {
        logger.info('Soft reconnect already in progress -- skipping duplicate call', {
            source: 'sip', event: 'soft_reconnect_skip_in_progress',
        });
        return;
    }

    try {
        const transport = (sipUserAgent as any).transport;
        if (transport && transport.socket) {
            isSoftReconnecting = true;

            logger.info('Reconnecting WebSocket transport layer', {
                source: 'sip', event: 'transport_reconnect',
            });

            transport.disconnect();

            // Safety timeout: reset isSoftReconnecting if the 1s reconnect
            // timer never fires (e.g. timer was garbage-collected or tab was
            // suspended). Without this, subsequent softReconnect calls would
            // be silently dropped forever.
            const safetyTimer = setTimeout(() => {
                if (isSoftReconnecting) {
                    logger.warn('Soft reconnect safety timeout -- resetting isSoftReconnecting flag', {
                        source: 'sip', event: 'soft_reconnect_safety_timeout',
                    });
                    isSoftReconnecting = false;
                }
            }, 5000);

            setTimeout(() => {
                isSoftReconnecting = false;
                clearTimeout(safetyTimer);
                transport.connect();
                logger.info('Transport layer reconnect initiated', {
                    source: 'sip', event: 'transport_reconnect_initiated',
                });
            }, 1000);
        } else {
            logger.warn('Soft reconnect skipped -- transport or socket not available', {
                source: 'sip', event: 'soft_reconnect_no_transport',
            });
        }
    } catch (error) {
        isSoftReconnecting = false;
        logger.error('Soft reconnect failed', {
            source: 'sip', event: 'soft_reconnect_failed',
            errorMessage: (error as Error)?.message,
        });
    }
};

const sessionTimingsMap = new WeakMap<RTCSession, SessionTimings>();
const getCallTimings = (s: RTCSession | null = session): SessionTimings => (s ? sessionTimingsMap.get(s) : undefined) ?? {};
const DEFAULT_DTMF_DURATION = 150;
const DEFAULT_DTMF_INTER_TONE_GAP = 500;

const ringingAudioRef = new Audio(ringtoneSound);
const busyAudioRef = new Audio (busySound);
const webrtcCompletedAudioRef = new Audio(webrtcCompletedSound);
webrtcCompletedAudioRef.volume = 0.8;
const webrtcDisconnectedAudioRef = new Audio(webrtcDisconnectedSound);

/** Tracks whether the "call connected" chime has already played for the current session. */
let connectedTonePlayed = false;

/**
 * Comfort tone — played when remote inbound audio stops flowing (silent call).
 * Mirrors mobile audioManager.startComfortTone(): connecting.mp3 at 0.25 vol, looping.
 * Stopped when flow resumes or call ends.
 */
const connectingAudioRef = new Audio(connectingSound);
connectingAudioRef.loop = true;
connectingAudioRef.volume = 0.25;
let isComfortTonePlaying = false;

const startComfortTone = () => {
    if (isComfortTonePlaying) return;
    isComfortTonePlaying = true;
    connectingAudioRef.currentTime = 0;
    connectingAudioRef.play().catch(() => { isComfortTonePlaying = false; });
    logger.info('Comfort tone started — remote audio stopped flowing', {
        source: 'sip', event: 'comfort_tone_started',
    });
};

const stopComfortTone = () => {
    if (!isComfortTonePlaying) return;
    isComfortTonePlaying = false;
    connectingAudioRef.pause();
    connectingAudioRef.currentTime = 0;
    logger.info('Comfort tone stopped — remote audio recovered', {
        source: 'sip', event: 'comfort_tone_stopped',
    });
};

/**
 * True once this session received a 180 Ringing and played ringback.
 * Suppresses the comfort tone on the first onInboundFlowChange(false) event —
 * the user already heard ringing so post-answer silence is expected.
 * Per-call: reset in ended/failed handlers.
 * Only applies to outgoing calls (ringback is outgoing-only).
 */
let ringbackWasPlaying = false;

const tabInstanceIds = new Map();
const TAB_ID = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const createAudioElement = () => {
    const audio = document.createElement('audio');
    audio.autoplay = true;
    audio.setAttribute('playsinline', '');
    audio.controls = false;
    audio.style.display = 'none';
    document.body.appendChild(audio);
    return audio;
};
const audioRef = createAudioElement();

const getSipInstanceId = (dispatch: AppDispatch) => {
    logger.debug('TAB_ID lookup', { source: 'sip', event: 'tab_id_lookup', tabId: TAB_ID });
    if (tabInstanceIds.has(TAB_ID)) {
        return tabInstanceIds.get(TAB_ID);
    }

    const newSipInstanceId = `${uuidv4()}`.toLowerCase();

    tabInstanceIds.set(TAB_ID, newSipInstanceId);

    logger.info('SIP instance ID generated', {
        source: 'sip',
        event: 'sip_instance_id_created',
        sipInstanceId: newSipInstanceId,
    });

    dispatch(setSipInstanceId({
        sipInstanceId: newSipInstanceId,
    }));

    return newSipInstanceId;
};

const cleanupAudio = (callId: string = 'unknown', s?: RTCSession | null) => {
    // Codex review — invalidate the per-session attach token (set in
    // setupAudioStream) so any still-pending track-handler awaits noop out
    // when they resume. Without this, a late attach can reattach srcObject
    // over a newer call's stream because audioRef is module-global.
    if (s) {
        (s as unknown as {__audioAttachToken?: symbol | null}).__audioAttachToken = null;
    }
    // Stop the periodic diagnostic tick for THIS callId only. Codex flagged
    // that a page-wide stop would let an old session's cleanup silently
    // disable a newer call's diagnostics. Keyed-by-callId stop is a no-op
    // for stale teardowns. Run BEFORE detachWorkletPath so the final tick
    // can't observe a half-torn-down graph.
    try { stopAudioDiagTick(callId); } catch { /* tearing down — swallow */ }
    try { stopAudioCallLogger(callId); } catch { /* tearing down — swallow */ }
    // Variant d teardown — tear down the AudioWorklet graph if it was attached.
    // No-op when the feature flag was off or feature-detect failed at attach
    // time. Done BEFORE clearing srcObject so the worklet's
    // MediaStreamAudioSourceNode sees the upstream end cleanly rather than a
    // yanked stream.
    try { detachWorkletPath(audioRef, callId); } catch { /* tearing down — swallow */ }
    if (audioRef.srcObject) {
        audioRef.srcObject = null;
    }
};

/**
 * Centralised per-session teardown. Codex flagged that the outbound and
 * inbound `failed` handlers were leaking — they didn't stop the audio health
 * monitor, didn't clear the tab-visibility / network-change cleanups, didn't
 * cleanupAudio(). The `ended` handlers did most of those things but copy-paste
 * drift between the two paths meant each one had its own subtly different
 * set. This helper makes both `failed` and `ended` go through one site.
 *
 * `resetRingback` is opt-in because some terminal events (failed) want to
 * reset the ringback-was-playing latch and some don't.
 */
const cleanupCallResources = (s: RTCSession | null | undefined, opts: {resetRingback?: boolean} = {}) => {
    const callId = getCallId(s);
    if (s && (s as unknown as {audioMonitor?: {stop: () => void}}).audioMonitor) {
        (s as unknown as {audioMonitor: {stop: () => void} | null}).audioMonitor!.stop();
        (s as unknown as {audioMonitor: {stop: () => void} | null}).audioMonitor = null;
    }
    if (visibilityCleanup) { visibilityCleanup(); visibilityCleanup = null; }
    if (networkChangeCleanup) { networkChangeCleanup(); networkChangeCleanup = null; }
    if (opts.resetRingback) ringbackWasPlaying = false;
    connectedTonePlayed = false;
    stopComfortTone();
    cleanupAudio(callId, s);
};

/**
 * Pre-seed NetEQ playout buffer on the audio receiver.
 *
 * WebRTC's default jitter buffer starts near-empty and ramps up only after
 * measuring path-delay variation live — on bad WiFi this produces 15–30 s of
 * audible stalls/stutter early in the call while NetEQ grows the target to
 * ~170 ms. Setting a floor of 120 ms at session start gives NetEQ headroom
 * from the first packet, at the cost of a little mouth-to-ear latency
 * (still well below the 300 ms ITU G.114 threshold).
 *
 * Deferred to the 'connected' ICE state: on inbound answers, touching
 * `receiver.jitterBufferTarget` during `addtrack` (which runs inside
 * setRemoteDescription(offer), BEFORE setLocalDescription(answer)) can
 * deadlock some libwebrtc versions. Mobile PR283 2e540b2 moved the call
 * out of `track` for exactly this reason — we mirror that here, even
 * though browser libwebrtc is less sensitive.
 *
 * ────────────────────────────────────────────────────────────────────────
 * !!  DO NOT MOVE THIS CALL INTO THE `track`/`addtrack` HANDLER.       !!
 *
 * It seems tempting — applying jitterBufferTarget as soon as the receiver
 * exists would protect the first second of audio on shaky joins, and
 * generic WebRTC docs say it's safe. An external review (codex 2026-05-15)
 * suggested it as a quick win and we tried it — second review correctly
 * flagged it as P1 hang risk citing this docstring. The deadlock is real,
 * observed in Mobile (PR283 2e540b2). Keep the deferred 'connected'
 * application. If you need earlier protection, talk to the mobile team
 * about whether their original incident still reproduces on current
 * Chromium and prove it's safe before changing this.
 * ────────────────────────────────────────────────────────────────────────
 *
 * `jitterBufferTarget` is a W3C spec property (milliseconds). Chromium
 * ships it, Firefox does not — wrap in try/catch + any-cast.
 */
const AUDIO_JITTER_BUFFER_TARGET_MS = 120;
const WORKLET_ATTACH_TIMEOUT_MS = 1500;

const applyAudioReceiverJitterTarget = (
    pc: RTCPeerConnection,
    callId: string,
    session: RTCSession,
): void => {
    if (isSessionEnded(session)) return;
    if (!getAudioFlags().receiverJitterTarget) return;
    const anyPc = pc as any;
    if (anyPc.__audioReceiverJitterTargetApplied) return;
    try {
        const audioReceiver = pc.getReceivers().find(r => r.track?.kind === 'audio');
        if (!audioReceiver) return;
        (audioReceiver as any).jitterBufferTarget = AUDIO_JITTER_BUFFER_TARGET_MS;
        anyPc.__audioReceiverJitterTargetApplied = true;
        logger.info('Audio receiver jitterBufferTarget set', {
            source: 'sip',
            event: 'audio_receiver_jitter_target_set',
            targetMs: AUDIO_JITTER_BUFFER_TARGET_MS,
            callId,
        });
    } catch (error) {
        logger.warn('Failed to set audio receiver jitterBufferTarget', {
            source: 'sip',
            event: 'audio_receiver_jitter_target_failed',
            callId,
            errorMessage: (error as Error)?.message,
        });
    }
};

const setupAudioStream = async (session: RTCSession, audioDeviceId: string) => {
    if (!session.connection) return;
    const callId: string = getCallId(session);
    const direction: string = session.direction ?? 'unknown';

    // Defer jitter-target application to the moment ICE reaches 'connected'
    // (both SDP sides set, audio receive pipeline stable). Per-PC guard
    // prevents re-application on ICE restart / re-INVITE.
    const pc = session.connection;

    // Codex review — stale-call guard. The `track` handler below awaits
    // multiple times (attachWorkletPath → setOutputDevice → audioRef.play()).
    // Without this, a late handler from a previous call could come back after
    // the call ended and re-attach `audioRef.srcObject` over a NEW call's
    // stream (audioRef is module-global). Or it could re-attach over a torn-
    // down audioRef and confuse the next session that picks it up.
    //
    // The token is per attach: cleanupAudio() nulls it out, isCurrentAttach()
    // returns false thereafter. Also re-check after every await so partial
    // setup unwinds cleanly (worklet path detached if it managed to attach
    // before we noticed teardown happened mid-await).
    const attachToken = Symbol(`audio-attach-${callId}`);
    (session as unknown as {__audioAttachToken?: symbol}).__audioAttachToken = attachToken;
    const isCurrentAttach = () =>
        !isSessionEnded(session) &&
        (session as unknown as {__audioAttachToken?: symbol}).__audioAttachToken === attachToken &&
        session.connection === pc;
    const onIceStateChange = () => {
        const state = pc.iceConnectionState;
        if (state === 'connected' || state === 'completed') {
            applyAudioReceiverJitterTarget(pc, callId, session);
        }
    };
    pc.addEventListener('iceconnectionstatechange', onIceStateChange);
    // Covers the case where the connection is already past 'connected' by the
    // time we attach (e.g. synchronous JsSIP peerconnection timing).
    if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        applyAudioReceiverJitterTarget(pc, callId, session);
    }

    // DSCP / WMM priority for outbound RTP — tells the OS and WiFi AP to
    // enqueue audio packets on the voice/EF queue instead of best-effort.
    // Chromium maps encodings[0].networkPriority to DSCP EF (46) and to WMM
    // AC_VO at the 802.11 layer when the network driver allows. Firefox
    // accepts the call but silently ignores it — wrap in try/catch.
    //
    // Retries until success: the first attempt fires immediately after
    // sipUserAgent.call()/session.answer(), and at that point the local
    // audio RTCRtpSender may not be attached yet or setParameters() can
    // throw during early negotiation. A second attempt runs in the 'track'
    // handler below once the PC is fully negotiated. The flag is flipped
    // only after a successful update so a transient failure doesn't lock
    // us out of the (idempotent) retry.
    const applySenderPriority = async (): Promise<boolean> => {
        if (isSessionEnded(session)) return true;
        if (!getAudioFlags().audioSenderPriority) return true;
        if ((session as any).__audioSenderPriorityApplied) return true;
        try {
            const audioSender = session.connection?.getSenders()
                .find(s => s.track?.kind === 'audio');
            if (!audioSender) {
                logger.debug('No audio sender yet — retrying on next signal', {
                    source: 'sip', event: 'audio_sender_priority_no_sender_yet',
                    callId, direction,
                });
                return false;
            }
            const params = audioSender.getParameters();
            const existing = (params.encodings && params.encodings.length > 0)
                ? params.encodings
                : [{}];
            const encodings = existing.map(e => ({
                ...e,
                networkPriority: 'high',
                priority: 'high',
            })) as RTCRtpEncodingParameters[];
            await audioSender.setParameters({ ...params, encodings });
            (session as any).__audioSenderPriorityApplied = true;
            logger.info('Audio sender priority set to high', {
                source: 'sip', event: 'audio_sender_priority_set',
                callId, direction,
            });
            return true;
        } catch (error) {
            logger.warn('Failed to set audio sender networkPriority — will retry', {
                source: 'sip', event: 'audio_sender_priority_failed',
                callId, direction,
                errorMessage: (error as Error)?.message,
            });
            return false;
        }
    };

    // Codex audio-quality pass — `MediaStreamTrack.contentHint = 'speech'`
    // tells Chromium's audio processing chain that the local capture is
    // conversational voice, which biases encoder choices and the AGC/NS
    // path toward speech intelligibility. Idempotent and retry-safe; same
    // sender-discovery pattern as applySenderPriority because the local
    // audio sender may not be attached on the first synchronous attempt.
    const applyLocalTrackContentHint = (): boolean => {
        if (isSessionEnded(session)) return true;
        const flag = session as unknown as {__audioContentHintApplied?: boolean};
        if (flag.__audioContentHintApplied) return true;
        try {
            const audioSender = session.connection?.getSenders()
                .find(s => s.track?.kind === 'audio');
            const track = audioSender?.track;
            if (!track) return false;
            if (track.contentHint === 'speech') {
                flag.__audioContentHintApplied = true;
                return true;
            }
            track.contentHint = 'speech';
            flag.__audioContentHintApplied = true;
            logger.info('Local audio track contentHint set', {
                source: 'sip', event: 'audio_track_content_hint_set',
                callId, direction,
            });
            return true;
        } catch (error) {
            logger.warn('Failed to set local audio contentHint', {
                source: 'sip', event: 'audio_track_content_hint_failed',
                callId, direction,
                errorMessage: (error as Error)?.message,
            });
            return false;
        }
    };

    const logLocalTrackSettings = (): boolean => {
        if (isSessionEnded(session)) return true;
        const flag = session as unknown as {__localTrackSettingsLogged?: boolean};
        if (flag.__localTrackSettingsLogged) return true;
        try {
            const audioSender = session.connection?.getSenders()
                .find(s => s.track?.kind === 'audio');
            const track = audioSender?.track;
            if (!track) return false;
            flag.__localTrackSettingsLogged = true;
            logger.info('Local audio track settings', {
                source: 'sip', event: 'local_audio_track_settings',
                callId, direction,
                ...describeLocalAudioTrack(track),
            });
            return true;
        } catch (error) {
            logger.warn('Failed to log local audio track settings', {
                source: 'sip', event: 'local_audio_track_settings_failed',
                callId, direction,
                errorMessage: (error as Error)?.message,
            });
            return false;
        }
    };

    let remoteAudioAttached = false;
    const attachRemoteAudio = async (
        remoteStream: MediaStream,
        audioTrack: MediaStreamTrack,
        attachSource: 'pc_track_event' | 'receiver_reconcile',
    ) => {
        if (remoteAudioAttached) return;
        if (!isCurrentAttach()) return;
        await applySenderPriority();
        applyLocalTrackContentHint();
        logLocalTrackSettings();
        if (!isCurrentAttach()) return;
        if (remoteAudioAttached) return;
        remoteAudioAttached = true;
        logger.info('Remote track received', {
            source: 'sip', event: 'remote_track_received',
            callId, direction, attachSource,
            trackKind: audioTrack.kind,
            trackEnabled: audioTrack.enabled,
            audioTrackCount: remoteStream.getAudioTracks().length,
            streamId: remoteStream.id,
        });
        let attachedViaWorklet = false;
            if (getAudioFlags().backgroundedAudioWorklet) {
                // Time-bound the worklet attach so a parked ctx.resume() /
                // addModule() can't leave srcObject unassigned. The prewarm
                // in makeCall() should make this a no-op in the happy path.
                const TIMEOUT_SENTINEL = Symbol('worklet-attach-timeout');
                type AttachRaceResult = boolean | typeof TIMEOUT_SENTINEL;
                let timerId: ReturnType<typeof setTimeout> | null = null;
                const timeout = new Promise<typeof TIMEOUT_SENTINEL>((resolve) => {
                    timerId = setTimeout(() => resolve(TIMEOUT_SENTINEL), WORKLET_ATTACH_TIMEOUT_MS);
                });
                let workletFallbackUsed = false;
                const attachPromise = attachWorkletPath(
                    audioRef, remoteStream, callId,
                    () => workletFallbackUsed || !isCurrentAttach(),
                );
                const raceResult: AttachRaceResult = await Promise.race([attachPromise, timeout]);
                if (raceResult === TIMEOUT_SENTINEL) {
                    workletFallbackUsed = true;
                    logger.warn('AudioWorklet attach timed out — using direct srcObject', {
                        source: 'sip', event: 'audio_worklet_attach_timeout',
                        callId, direction, timeoutMs: WORKLET_ATTACH_TIMEOUT_MS,
                    });
                    void attachPromise.then((lateAttached) => {
                        logger.info('AudioWorklet attach resolved after timeout', {
                            source: 'sip', event: 'audio_worklet_attach_post_timeout',
                            callId, direction, lateAttached,
                        });
                    });
                } else {
                    if (timerId !== null) clearTimeout(timerId);
                    attachedViaWorklet = raceResult;
                }
                // Re-check after the worklet attach awaits. If the call ended
                // during ctx.resume() / addModule() / wire-up, undo the partial
                // attach so the next call can claim audioRef cleanly.
                if (!isCurrentAttach()) {
                    if (attachedViaWorklet) detachWorkletPath(audioRef, callId);
                    return;
                }
            }
            if (!attachedViaWorklet) {
                if (!isCurrentAttach()) return;
                audioRef.srcObject = remoteStream;
            }
            await setOutputDevice(audioRef, audioDeviceId, callId);
            if (!isCurrentAttach()) return;

            const playOutcome = await loggedPlay(audioRef, callId, 'remote_track');
            if (!isCurrentAttach()) return;
            if (playOutcome.ok) {
                logger.info('Audio playback started', {
                    source: 'sip', event: 'audio_play_started',
                    callId, direction, audioDeviceId,
                    workletRouted: attachedViaWorklet,
                });
                // Codex review — start the periodic diagnostic snapshot and
                // per-call state loggers. Both stop via cleanupAudio() /
                // cleanupCallResources(), keyed by callId. Started here
                // (not at attach time) so we only emit for calls that
                // actually reached playback.
                startAudioDiagTick({callId, pc, audioElement: audioRef});
                startAudioCallLogger({
                    callId, pc,
                    remoteAudioTrack: audioTrack,
                    audioElement: audioRef,
                });
            } else {
                logger.warn('Autoplay failed — user interaction may be required', {
                    source: 'sip', event: 'audio_play_failed',
                    callId, direction,
                    errorMessage: playOutcome.errorMessage,
                });
            }
    };

    const reconcileRemoteAudioReceiver = (phase: string) => {
        if (remoteAudioAttached) return;
        if (!isCurrentAttach()) return;
        // Synthetic-stream attach risks beating the real track event; only needed on the anchored path.
        if (!needsRemoteStreamAnchor()) return;
        const audioTrack = pc.getReceivers()
            .find(r => r.track?.kind === 'audio' && r.track.readyState !== 'ended')
            ?.track;
        if (!audioTrack) {
            logger.debug('Remote audio receiver reconcile — no track', {
                source: 'sip', event: 'audio_receiver_reconcile_empty',
                callId, direction, phase,
                receiverCount: pc.getReceivers().length,
            });
            return;
        }
        // A muted track outputs silence through any consumer (MediaStreamAudioSourceNode
        // → worklet → speaker = zeros). Wait for either the PC 'track' event or
        // the track's 'unmute' event before attaching.
        if (audioTrack.muted) {
            logger.debug('Remote audio receiver reconcile — track muted, deferring', {
                source: 'sip', event: 'audio_receiver_reconcile_muted',
                callId, direction, phase,
                trackReadyState: audioTrack.readyState,
                trackEnabled: audioTrack.enabled,
            });
            const onUnmute = () => {
                audioTrack.removeEventListener('unmute', onUnmute);
                if (remoteAudioAttached || !isCurrentAttach()) return;
                logger.info('Remote audio receiver reconcile — track unmuted, attaching', {
                    source: 'sip', event: 'audio_receiver_reconcile_unmute_attach',
                    callId, direction, phase,
                });
                void attachRemoteAudio(new MediaStream([audioTrack]), audioTrack, 'receiver_reconcile');
            };
            audioTrack.addEventListener('unmute', onUnmute);
            return;
        }
        logger.info('Remote audio receiver reconcile — attaching track', {
            source: 'sip', event: 'audio_receiver_reconcile_attach',
            callId, direction, phase,
            trackReadyState: audioTrack.readyState,
            trackMuted: audioTrack.muted,
            trackEnabled: audioTrack.enabled,
        });
        void attachRemoteAudio(new MediaStream([audioTrack]), audioTrack, 'receiver_reconcile');
    };

    session.connection.addEventListener('track', async (event: RTCTrackEvent) => {
        logger.info('PC track event received', {
            source: 'sip', event: 'audio_pc_track_event',
            callId, direction,
            trackKind: event.track.kind,
            trackReadyState: event.track.readyState,
            trackMuted: event.track.muted,
            trackEnabled: event.track.enabled,
            streamsLength: event.streams.length,
            signalingState: pc.signalingState,
            iceConnectionState: pc.iceConnectionState,
        });
        if (event.track.kind !== 'audio') return;
        const remoteStream = event.streams[0] ?? new MediaStream([event.track]);
        // Deferring breaks the WebRTC→WebAudio handoff in element mode (Windows source pumps zeros).
        if (event.track.muted && needsRemoteStreamAnchor()) {
            logger.debug('PC track event — track muted, deferring attach until unmute', {
                source: 'sip', event: 'audio_pc_track_muted_defer',
                callId, direction,
            });
            const onUnmute = () => {
                event.track.removeEventListener('unmute', onUnmute);
                if (remoteAudioAttached || !isCurrentAttach()) return;
                logger.info('PC track unmuted — attaching', {
                    source: 'sip', event: 'audio_pc_track_unmute_attach',
                    callId, direction,
                });
                void attachRemoteAudio(remoteStream, event.track, 'pc_track_event');
            };
            event.track.addEventListener('unmute', onUnmute);
            return;
        }
        await attachRemoteAudio(remoteStream, event.track, 'pc_track_event');
    });

    await applySenderPriority();
    applyLocalTrackContentHint();
    logLocalTrackSettings();

    reconcileRemoteAudioReceiver('immediate');
    [100, 500, 1500, 3000].forEach(ms =>
        setTimeout(() => reconcileRemoteAudioReceiver(`${ms}ms`), ms),
    );
};

const setOutputDevice = async (audioElement: HTMLAudioElement, audioDeviceId: string, callId = 'unknown') => {
    if (typeof audioElement.setSinkId !== 'undefined') {
        try {
            await audioElement.setSinkId(audioDeviceId);
            logger.info('Audio output device attached', {
                source: 'sip', event: 'audio_sink_set',
                callId, audioDeviceId,
            });
        } catch (error) {
            logger.error('Failed to attach audio output device', {
                source: 'sip', event: 'audio_sink_error',
                callId, audioDeviceId,
                errorMessage: (error as Error)?.message,
            });
        }
    } else {
        logger.warn('Browser does not support setSinkId — output device selection unavailable', {
            source: 'sip', event: 'audio_sink_unsupported', callId,
        });
    }
};
interface EventHandlers {
    [key: string]: (e: any) => void;
}
const createDefaultEventHandlers = (dispatch: AppDispatch): EventHandlers => {
    dispatch = tagEngine('sip', dispatch);
    const ringbackManager = useMemo(() => RingbackManager.getInstance(), []);
    return {
        'progress': (e: any) => {
            if(e.response.status_code === 180) {
                if(!store.getState().dialer.isRingbackPlaying) {
                    dispatch(playRingback());
                    ringbackManager.play();
                }
                ringbackWasPlaying = true;
            }
            if (e.response.status_code === 183) {
                    dispatch(stopRingback());
            }
            dispatch(setCallState(CallStateEnum.CONNECTING));
        },
        'failed': (e: any) => {
          ongoingCalls = Math.max(0, ongoingCalls - 1);
            logger.warn('Outgoing call failed', {
                source: 'sip',
                event: 'call_failed',
                direction: 'outgoing',
                callId: (session as any)?.id,
                cause: e?.cause,
                statusCode: e?.message?.status_code,
            });
            if(e.cause == 'Busy' || e.cause == 'Unavailable') {
                busyAudioRef.play()
            }
            if (getAudioFlags().iceServersCacheAndPrefetch) {
                clearIceServerCache();
                prefetchIceServers();
            }
            dispatch(setCallFrom(null));
            dispatch(setCallState(CallStateEnum.IDLE));
            dispatch(stopRingback());
            // Codex review — pre-fix this handler skipped audioMonitor.stop(),
            // visibilityCleanup, and cleanupAudio(). The AudioWorklet graph,
            // health-monitor timers, and tab-visibility listeners would leak
            // for every failed outbound call. Route through the shared
            // helper so this stays in sync with the 'ended' path.
            cleanupCallResources(session, {resetRingback: true});
        },
        'getusermediafailed': (err: MediaAccessError) => {
            logger.error('getUserMedia failed', {
                source: 'sip',
                event: 'getusermedia_failed',
                direction: 'outgoing',
                callId: session?.id,
                errorName: err?.name,
                errorMessage: err?.message,
                constraint: err?.constraint,
            });
        },
        'ended': (e: any) => {
          ongoingCalls = Math.max(0, ongoingCalls - 1);
            logger.info('Outgoing call ended', {
                source: 'sip',
                event: 'call_ended',
                direction: 'outgoing',
                callId: (session as any)?.id,
                cause: e?.cause,
            });
            if (getAudioFlags().iceServersCacheAndPrefetch) {
                clearIceServerCache();
                prefetchIceServers();
            }
            dispatch(setCallFrom(null));
            connectedTonePlayed = false;
            ringbackWasPlaying = false;
            stopComfortTone();
            if (getAudioFlags().lifecycleChimes) {
                webrtcDisconnectedAudioRef.currentTime = 0;
                webrtcDisconnectedAudioRef.play().catch(() => {});
            }
            dispatch(setCallState(CallStateEnum.IDLE));
            dispatch(stopRingback());
            if ((session as any).audioMonitor) {
                (session as any).audioMonitor.stop();
                (session as any).audioMonitor = null;
            }
            if (visibilityCleanup) { visibilityCleanup(); visibilityCleanup = null; }
            if (networkChangeCleanup) { networkChangeCleanup(); networkChangeCleanup = null; }
            cleanupAudio(getCallId(session), session);
        },
        'accepted': (_e: any) => {
            getCallTimings(session).acceptedAt ??= Date.now();
        },
        'confirmed': (_e: any) => {
            const timings = getCallTimings(session);
            timings.confirmedAt ??= Date.now();
            const acceptedToConfirmedMs = timings.acceptedAt
                ? timings.confirmedAt - timings.acceptedAt
                : null;
            logger.info('Outgoing call confirmed', {
                source: 'sip',
                event: 'call_confirmed',
                direction: 'outgoing',
                acceptedToConfirmedMs,
            });
            dispatch(setCallState(CallStateEnum.IN_CALL));
            dispatch(stopRingback());
            const flags = getAudioFlags();
            if (flags.audioHealthMonitor && session?.connection) {
                const callId: string = getCallId(session);
                const monitor = createAudioHealthMonitor(
                    session.connection,
                    { pollMs: 2000, windowSeconds: 10 },
                    dispatch,
                    getCallTimings(session),
                    callId,
                    (flowing) => {
                        if (flowing) {
                            stopComfortTone();
                            if (!connectedTonePlayed) {
                                connectedTonePlayed = true;
                                if (flags.lifecycleChimes) {
                                    webrtcCompletedAudioRef.currentTime = 0;
                                    webrtcCompletedAudioRef.play().catch(() => {});
                                }
                            }
                        } else {
                            // Suppress comfort tone if ringback was playing in this call.
                            // The user already heard ringing so brief post-answer silence
                            // is expected; the monitor will re-fire if audio stays stuck.
                            if (!ringbackWasPlaying) {
                                if (flags.comfortTone) startComfortTone();
                            } else {
                                // Clear the flag after the first suppression — if audio
                                // stays stuck through a full second poll, play the tone.
                                ringbackWasPlaying = false;
                            }
                        }
                        logger.info(flowing ? 'Inbound audio started flowing' : 'Inbound audio stopped', {
                            source: 'sip',
                            event: flowing ? 'audio_flow_start' : 'audio_flow_stop',
                            direction: 'outgoing',
                            callId,
                        });
                    },
                );
                (session as any).audioMonitor = monitor;
            }
            // Register tab visibility handler — Chrome throttles background tabs
            // which can degrade audio quality when users switch between tabs.
            if (visibilityCleanup) { visibilityCleanup(); visibilityCleanup = null; }
            if (flags.tabVisibilityHandler) {
                visibilityCleanup = setupTabVisibilityHandling(session!, getCallId(session));
            }
            // Register network change handler for ICE recovery during call
            if (networkChangeCleanup) { networkChangeCleanup(); networkChangeCleanup = null; }
            if (flags.networkChangeHandler) {
                networkChangeCleanup = setupNetworkChangeHandling(
                    session,
                    softReconnectTransport,
                    sipUserAgent,
                    (session as any)?.id,
                );
            }
        }
    };
};

export const SIP_AUTH_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 16000, 30000];
export const SIP_MAX_AUTH_FAILURES = 10;

export const SIP_RETRY_AFTER_MIN_MS = 1000;
export const SIP_RETRY_AFTER_MAX_MS = 300_000;

export const parseRetryAfterMs = (response: unknown): number | undefined => {
    const getHeader = (response as {getHeader?: (name: string) => unknown} | null | undefined)?.getHeader;
    if (typeof getHeader !== 'function') {
        return undefined;
    }
    let raw: unknown;
    try {
        raw = getHeader.call(response, 'Retry-After');
    } catch {
        return undefined;
    }
    if (typeof raw !== 'string') {
        return undefined;
    }
    const match = /^(\d{1,9})(?=$|[\s;(])/.exec(raw.trim());
    if (!match) {
        return undefined;
    }
    return Math.min(SIP_RETRY_AFTER_MAX_MS, Math.max(SIP_RETRY_AFTER_MIN_MS, Number(match[1]) * 1000));
};

let disposeSipAuthRecovery: (() => void) | null = null;

export const stopSipAuthRecovery = (): void => {
    disposeSipAuthRecovery?.();
    disposeSipAuthRecovery = null;
};

const initializeSip = (dispatch: any, audioDeviceId: string) => {
    if (getAccountBlocked()) {
        return;
    }
    dispatch = tagEngine('sip', dispatch);
    const socket = new JsSIP.WebSocketInterface(window.__ENV__?.URL_WSS_VOIP);

    JsSIP.debug.enable("JsSIP:*");

    const tenant = store.getState().auth.tenant;
    const user = store.getState().auth.userId
    const token = getAccessToken();
    const sipInstanceId = store.getState().dialer.sipInstanceId;

    logger.info('SIP UA initializing', {
        source: 'sip',
        event: 'sip_ua_init',
        tenant: tenant ?? undefined,
        user: user ?? undefined,
        sipInstanceId: sipInstanceId ?? undefined,
    });


    let retryCount = 0;
    const maxRetries = 5;
    const retryDelay = 1000;

    const userAgent = `JsSIP ${JsSIP.version} | ${navigator.userAgent} | phq-ab-${getVariant() ?? 'c'}`;

    const configuration = {
        sockets: [socket],
        uri: `sip:${user}@${tenant}`,
        password: user,
        session_timers: false,
        instance_id: getSipInstanceId(dispatch),
        authorization_jwt: token,
        user_agent: userAgent,
        extra_headers: [`P-User-Agent: ${userAgent}`],
    };

    const ua = new JsSIP.UA(configuration as any);
    sipUserAgent = ua;

    // Pre-warm TURN credentials so they're cached before any incoming call arrives
    if (getAudioFlags().iceServersCacheAndPrefetch) {
        prefetchIceServers();
    }

    const startRegistration = () => {
        if (getAccountBlocked()) {
            return;
        }
        logger.info('SIP registration attempt', { source: 'sip', event: 'sip_register_attempt', retryCount });
        ua.start();
    };

    startRegistration();

    let authFailureCount = 0;
    let authRetryTimer: ReturnType<typeof setTimeout> | null = null;
    let authRetryStopped = false;
    let authSessionEnded = false;
    let authRecoveryDisposed = false;
    let registrationRetryTimer: ReturnType<typeof setTimeout> | null = null;

    const clearAuthRetryTimer = () => {
        if (authRetryTimer !== null) {
            clearTimeout(authRetryTimer);
            authRetryTimer = null;
        }
    };

    const registerWithCurrentToken = () => {
        if (authRecoveryDisposed || getAccountBlocked() || authSessionEnded) {
            return;
        }
        const currentToken = getAccessToken();
        if (currentToken) {
            ua.set('authorization_jwt', currentToken);
        }
        ua.register();
    };

    const refreshThenRegister = async () => {
        authRetryTimer = null;
        if (authRecoveryDisposed || getAccountBlocked() || authSessionEnded) {
            return;
        }
        try {
            await refreshAccessToken();
        } catch (error) {
            if (authRecoveryDisposed) {
                return;
            }
            if (error instanceof SessionEndedError) {
                authSessionEnded = true;
                logger.warn('SIP auth retry stopped, session ended', {
                    source: 'sip',
                    event: 'sip_auth_retry_session_ended',
                });
                return;
            }
            logger.warn('Token refresh before SIP re-register failed', {
                source: 'sip',
                event: 'sip_auth_refresh_failed',
                error: error instanceof Error ? error.message : String(error),
            });
        }
        if (authRecoveryDisposed) {
            return;
        }
        registerWithCurrentToken();
    };

    const resumeAuthRecovery = (refreshFirst: boolean) => {
        if (authRecoveryDisposed || authSessionEnded || (authRetryTimer === null && !authRetryStopped)) {
            return;
        }
        clearAuthRetryTimer();
        authRetryStopped = false;
        if (refreshFirst) {
            void refreshThenRegister();
        } else {
            registerWithCurrentToken();
        }
    };

    const onTokenChanged = () => resumeAuthRecovery(false);
    const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            resumeAuthRecovery(true);
        }
    };
    const onOnline = () => resumeAuthRecovery(true);

    disposeSipAuthRecovery?.();
    const unsubscribeTokenChanges = subscribeToAccessTokenChanges(onTokenChanged);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', onOnline);
    disposeSipAuthRecovery = () => {
        authRecoveryDisposed = true;
        clearAuthRetryTimer();
        if (registrationRetryTimer !== null) {
            clearTimeout(registrationRetryTimer);
            registrationRetryTimer = null;
        }
        unsubscribeTokenChanges();
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('online', onOnline);
    };

    ua.on('registered', () => {
        retryCount = 0;
        authFailureCount = 0;
        authRetryStopped = false;
        authSessionEnded = false;
        clearAuthRetryTimer();
        logger.info('SIP registered successfully', { source: 'sip', event: 'sip_registered' });
    });

    ua.on('registrationFailed', async e => {
        logger.error('SIP registration failed', {
            source: 'sip',
            event: 'sip_registration_failed',
            cause: e.cause,
            statusCode: e.response?.status_code,
            retryCount,
        });

        if (
            e.cause === JsSIP.C.causes.AUTHENTICATION_ERROR ||
            (e.cause === JsSIP.C.causes.REJECTED &&
                (e.response?.status_code === 401 ||
                    e.response?.status_code === 403))
        ) {
            if (ua.isRegistered()) {
                ua.unregister();
            }

            authFailureCount += 1;
            clearAuthRetryTimer();
            if (authFailureCount >= SIP_MAX_AUTH_FAILURES) {
                authRetryStopped = true;
                logger.error('SIP auth retries exhausted', {
                    source: 'sip',
                    event: 'sip_auth_retry_exhausted',
                    authFailureCount,
                });
            } else {
                const delayMs = Math.max(
                    SIP_AUTH_RETRY_DELAYS_MS[Math.min(authFailureCount, SIP_AUTH_RETRY_DELAYS_MS.length) - 1],
                    parseRetryAfterMs(e.response) ?? 0,
                );
                authRetryTimer = setTimeout(() => {
                    void refreshThenRegister();
                }, delayMs);
            }
        } else if (retryCount < maxRetries) {
            retryCount++;
            const nonAuthDelayMs = Math.max(retryDelay, parseRetryAfterMs(e.response) ?? 0);
            logger.info('Retrying SIP registration', {
                source: 'sip',
                event: 'sip_register_retry',
                retryCount,
                maxRetries,
                retryDelayMs: nonAuthDelayMs,
            });
            registrationRetryTimer = setTimeout(() => {
                registrationRetryTimer = null;
                if (authRecoveryDisposed) {
                    return;
                }
                startRegistration();
            }, nonAuthDelayMs);
        } else {
            dispatch(navigateDialer('/error/sip-error'));
            logger.error('Max SIP registration retries reached', {
                source: 'sip',
                event: 'sip_register_max_retries',
                maxRetries,
            });
        }

        session = null;
    });

    ua.on("newRTCSession", (data: any) => {
        if (shouldRejectIncomingSipAsBusy(data.session?.direction, isGroupCallActive(), ongoingCalls)) {
            rejectIncomingAsBusy(data.session);
            return;
        }
        session = data.session;
        const currentSession = session; // capture for closures — module-level `session` may be overwritten by the next call
        if (currentSession) {
            trackSipCall(currentSession, getCallId(currentSession), currentSession.direction === 'incoming' ? 'inbound' : 'outbound');
            const publishLater = () => {
                setTimeout(publishSipControls, 0);
            };
            currentSession.on('hold', publishLater);
            currentSession.on('unhold', publishLater);
            currentSession.on('muted', publishLater);
            currentSession.on('unmuted', publishLater);
            currentSession.on('confirmed', publishLater);
            currentSession.on('ended', publishLater);
            currentSession.on('failed', publishLater);
        }
        publishSipControls();
        const request = data.request;
        ongoingCalls++;
        // Create isolated per-call timings keyed by session instance
        if (currentSession) sessionTimingsMap.set(currentSession, {});
        // Hook JsSIP's `sdp` event so our Opus tuning actually reaches the
        // wire. The `sdpTransform` option passed to ua.call()/session.answer()
        // is NOT consumed by JsSIP 3.x and has been a silent no-op on this
        // branch — without this hook, variant-C DTX/FMTP/NACK would never
        // ship. Fires for both directions (offer from UA.call(), answer from
        // session.answer()) and filters to local originator inside the hook.
        if (currentSession) {
            installSdpMangling(currentSession, currentSession.direction === 'incoming' ? 'incoming' : 'outgoing');
        }
        let associatedContacts = null;
        let callTo = null;
        let ivr = null;
        let spam = null;
        let isEns = false;
        try {
            const contactsHeader = request.getHeader('P-Contacts');
            const destinationNumberHeader = request.getHeader('P-Number');
            const isSpamHeader = request.getHeader('P-is-spam');
            const ensHeader = request.getHeader('P-ENS');
            isEns = ensHeader?.trim()?.toLowerCase() === 'true';
            const ivrHeader = request.getHeader('P-Ivr')?.split(';')?.[2]
            if(ivrHeader) {
                ivr = atob(ivrHeader);
            }
            if(isSpamHeader) {
                spam = isSpamHeader;
            }
            
            if (contactsHeader) associatedContacts = JSON.parse(contactsHeader);
            if (destinationNumberHeader) callTo = JSON.parse(destinationNumberHeader);

        } catch (e) {
            logger.warn('Failed to parse P- headers in newRTCSession', {
                source: 'sip',
                event: 'new_rtc_session_header_parse_error',
                errorMessage: (e as Error)?.message,
            });
        }

        logger.info('New RTC session', {
            source: 'sip',
            event: 'new_rtc_session',
            callId: (currentSession as any)?.id,
            direction: currentSession?.direction,
            remoteUser: currentSession?.remote_identity?.uri?.user,
            callTo: callTo?.msisdn,
            hasContacts: !!associatedContacts?.primary,
            ongoingCalls,
        });

        let callFrom: string | undefined;
        if(callTo?.msisdn) {
            dispatch(setCallTo(callTo));
        }
        if (associatedContacts?.primary) {
            callFrom = `${associatedContacts.primary?.firstName} ${associatedContacts.primary?.lastName}`
            dispatch(setCallFrom({...associatedContacts.primary, msisdn: associatedContacts.primary?.msisdn ?? currentSession?.remote_identity?.uri?.user}));
            dispatch(setSpam(spam));
        } else if (!engineCallOf(store.getState().dialer, 'sip')?.callFrom) {
            if (isEns) {
                const remoteDisplayName = session?.remote_identity?.display_name?.trim();
                callFrom = remoteDisplayName || session?.remote_identity?.uri?.user
            } else {
                callFrom = session?.remote_identity?.uri?.user
            }

            dispatch(setCallFrom(callFrom));
        }
            dispatch(setIvrKeyName(ivr));
            dispatch(setSpam(spam))
        if (currentSession?.direction === "incoming") {
            // Setup smart ICE candidate handling for incoming calls
            setupICECandidateHandling(currentSession, getCallTimings(currentSession), hasCachedTurnServers());

            // Show browser notification
            if ("Notification" in window) {
                if (Notification.permission === "granted") {
                    const notification = new Notification("Incoming Call", {
                        body: `Incoming call from ${callFrom}`,
                        icon: logo
                    });
                    notification.onclick = () => {
                        focusAppWindow();
                        notification.close();
                    }
                } else if (Notification.permission !== "denied") {
                    Notification.requestPermission().then(permission => {
                        if (permission === "granted") {
                            new Notification("Incoming Call", {
                                body: `Incoming call from ${callFrom}`,
                                icon: logo
                            });
                        }
                    });
                }
            }

            ringingAudioRef.play();

            dispatch(setDialerHidden(false));
            dispatch(setIncomingCall(true));
             const autoHideDialer = store.getState().dialer.autoHideDialer

            currentSession.on("accepted", async () => {
                getCallTimings(currentSession).acceptedAt ??= Date.now();
                ringingAudioRef.pause();
                await setOutputDevice(audioRef, audioDeviceId, (currentSession as any)?.id);
                dispatch(setCallState(CallStateEnum.IN_CALL));
            });
            currentSession.on("confirmed", async () => {
                const timings = getCallTimings(currentSession);
                timings.confirmedAt ??= Date.now();
                const acceptedToConfirmedMs = timings.acceptedAt
                    ? timings.confirmedAt - timings.acceptedAt
                    : null;
                logger.info('Incoming call confirmed', {
                    source: 'sip',
                    event: 'call_confirmed',
                    direction: 'incoming',
                    callId: currentSession.id,
                    acceptedToConfirmedMs,
                });
                ringingAudioRef.pause();
                await setOutputDevice(audioRef, audioDeviceId, (currentSession as any)?.id);
                dispatch(setCallState(CallStateEnum.IN_CALL));
                const flags = getAudioFlags();
                if (flags.audioHealthMonitor && currentSession?.connection) {
                    const callId: string = getCallId(currentSession);
                    const monitor = createAudioHealthMonitor(
                        currentSession.connection,
                        { pollMs: 2000, windowSeconds: 10 },
                        dispatch,
                        getCallTimings(currentSession),
                        callId,
                        (flowing) => {
                            if (flowing) {
                                stopComfortTone();
                                if (!connectedTonePlayed) {
                                    connectedTonePlayed = true;
                                    if (flags.lifecycleChimes) {
                                        webrtcCompletedAudioRef.currentTime = 0;
                                        webrtcCompletedAudioRef.play().catch(() => {});
                                    }
                                }
                            } else {
                                // Incoming calls never play ringback, so no suppression needed.
                                if (flags.comfortTone) startComfortTone();
                            }
                            logger.info(flowing ? 'Inbound audio started flowing' : 'Inbound audio stopped', {
                                source: 'sip',
                                event: flowing ? 'audio_flow_start' : 'audio_flow_stop',
                                direction: 'incoming',
                                callId,
                            });
                        },
                    );
                    (currentSession as any).audioMonitor = monitor;
                }
                // Register tab visibility handler — Chrome throttles background tabs
                if (visibilityCleanup) { visibilityCleanup(); visibilityCleanup = null; }
                if (flags.tabVisibilityHandler) {
                    visibilityCleanup = setupTabVisibilityHandling(currentSession, getCallId(currentSession));
                }
                // Register network change handler for ICE recovery during call
                if (networkChangeCleanup) { networkChangeCleanup(); networkChangeCleanup = null; }
                if (flags.networkChangeHandler) {
                    networkChangeCleanup = setupNetworkChangeHandling(
                        currentSession,
                        softReconnectTransport,
                        sipUserAgent,
                        (currentSession as any)?.id,
                    );
                }
            });
            currentSession.on("ended", (e: any) => {
              ongoingCalls = Math.max(0, ongoingCalls - 1);
                logger.info('Incoming call ended', {
                    source: 'sip',
                    event: 'call_ended',
                    direction: 'incoming',
                    callId: (currentSession as any)?.id,
                    cause: e?.cause,
                });
                // Clear TURN cache so next call gets fresh credentials.
                // Cloudflare TURN limits concurrent allocations per credential,
                // so reusing the same credential for back-to-back calls causes
                // relay candidates to be silently dropped.
                if (getAudioFlags().iceServersCacheAndPrefetch) {
                    clearIceServerCache();
                    prefetchIceServers();
                }
                dispatch(setCallState(CallStateEnum.IDLE));
                dispatch(setIncomingCall(false));
                dispatch(setCallFrom(null));
                dispatch(setCallTo(null));
                connectedTonePlayed = false;
                stopComfortTone();
                if (getAudioFlags().lifecycleChimes) {
                    webrtcDisconnectedAudioRef.currentTime = 0;
                    webrtcDisconnectedAudioRef.play().catch(() => {});
                }
                dispatch(setIvrKeyName(''));
                dispatch(setSpam(''));
                if(autoHideDialer) {
                    dispatch(setDialerHidden(true));
                }
                if ((currentSession as any).audioMonitor) {
                    (currentSession as any).audioMonitor.stop();
                    (currentSession as any).audioMonitor = null;
                }
                if (visibilityCleanup) { visibilityCleanup(); visibilityCleanup = null; }
                if (networkChangeCleanup) { networkChangeCleanup(); networkChangeCleanup = null; }
                // Variant d: tear down the AudioWorklet graph for this call.
                const incCallId = getCallId(currentSession);
                cleanupAudio(incCallId, currentSession);
            });
            currentSession.on("failed", (e: any) => {
              ongoingCalls = Math.max(0, ongoingCalls - 1);
                logger.warn('Incoming call failed', {
                    source: 'sip',
                    event: 'call_failed',
                    direction: 'incoming',
                    callId: (currentSession as any)?.id,
                    cause: e?.cause,
                    statusCode: e?.message?.status_code,
                });
                if (getAudioFlags().iceServersCacheAndPrefetch) {
                    clearIceServerCache();
                    prefetchIceServers();
                }
                ringingAudioRef.pause();
                stopComfortTone();
                dispatch(setCallState(CallStateEnum.IDLE));
                dispatch(setIncomingCall(false));
                dispatch(setCallFrom(null));
                dispatch(setCallTo(null));
                dispatch(setIvrKeyName(''));
                dispatch(setSpam(''));
                if(autoHideDialer) {
                    dispatch(setDialerHidden(true));
                }
                // Codex review — pre-fix this handler called only
                // networkChangeCleanup, leaving audioMonitor, visibilityCleanup,
                // and the AudioWorklet graph alive after a failed inbound call.
                // Route through the shared helper so failed and ended paths
                // tear down the same set of resources.
                cleanupCallResources(currentSession);
            });
            currentSession.on("getusermediafailed", (err: MediaAccessError) => {
                logger.error('getUserMedia failed', {
                    source: 'sip',
                    event: 'getusermedia_failed',
                    direction: 'incoming',
                    callId: currentSession.id,
                    errorName: err?.name,
                    errorMessage: err?.message,
                    constraint: err?.constraint,
                });
            });
        }
    });

    // --- Answer OPTIONS depending on call state (fix: gate by originator) ---
    ua.on('newOptions', (e: any) => {
        // Only handle **incoming** OPTIONS
        if (e.originator !== 'remote') return;

        const req = e.request; // JsSIP.IncomingRequest (has reply())
        // Keep headers lean; add more if your infra expects them
        const busy = isSipBusy({ongoingCalls, groupCallActive: isGroupCallActive()});
        const extraHeaders = [
            'Allow: INVITE, ACK, CANCEL, BYE, UPDATE, OPTIONS, MESSAGE, INFO',
            'Accept: application/sdp',
            `P-Call-State: ${busy ? 'busy' : 'idle'}`,
            `P-Call-Counter: ${ongoingCalls}`
        ];

        if (busy) {
            req.reply(200, 'OK', extraHeaders);
        } else {
            req.reply(481, 'Call/Transaction Does Not Exist', extraHeaders);
        }
    });
// -----------------------------------------------------------------------


// Request notification permission on application start
    if ("Notification" in window && Notification.permission !== "granted" && Notification.permission !== "denied") {
        Notification.requestPermission();
    }
};

const call = async (phoneNumber: string | undefined, options: any, audioDeviceId: string) => {
    if (!phoneNumber) {
        logger.warn('Outgoing call aborted — phone number missing', { source: 'sip', event: 'call_aborted', reason: 'no_phone_number' });
        return;
    }
    if (!sipUserAgent) {
        logger.warn('Outgoing call aborted — SIP UA not ready', { source: 'sip', event: 'call_aborted', reason: 'ua_not_ready' });
        return;
    }

    // Guard against double-invocation (e.g. double-click on call button).
    // Two concurrent call() executions fire two INVITEs; the second session
    // overwrites the module-level `session`, and when it fails the handler
    // resets UI while the first call is still alive and unreachable.
    if (_callInProgress) {
        logger.warn('Outgoing call aborted — another call() is already in progress', {
            source: 'sip', event: 'call_aborted', reason: 'duplicate_call',
        });
        return;
    }
    _callInProgress = true;

    // Clear abort flag from any previous call
    _callAborted = false;
    // Note: teardown state lives on the RTCSession itself (__callEnded +
    // JsSIP _status). A new session starts fresh so no module-level reset
    // is required for back-to-back calls.

    const tenant = store.getState().auth.tenant;

    // Outgoing call: force-refresh TURN credentials so the first call and any
    // back-to-back call always start with a fresh allocation. endCall() also
    // clears on hangup, but this covers the first-ever call in a session where
    // credentials may have been pre-fetched long before the call was made.
    if (getAudioFlags().iceServersCacheAndPrefetch) {
        clearIceServerCache();
    }

    // Get ICE servers (fetched dynamically from calling service)
    const iceServers = await getIceServers();

    // If the user hung up while we were fetching TURN credentials, abort
    // before sending the INVITE. Without this guard, a ghost INVITE fires
    // after the UI already cleaned up — the remote user sees an incoming call
    // while the caller's UI is idle.
    if (_callAborted) {
        _callInProgress = false;
        logger.warn('Outgoing call aborted — user hung up during TURN fetch', {
            source: 'sip', event: 'call_aborted', reason: 'user_hangup_during_turn_fetch',
        });
        return;
    }

    // BBB pattern #2 — operators can flip ?relay=on / phq_force_relay
    // localStorage to force TURN-only call paths when host/srflx candidates
    // are negotiating successfully but dropping media mid-call. Default off.
    //
    // Safety gate: only force relay if we actually have TURN servers. If
    // getIceServers() fell back to PUBLIC_STUN_FALLBACK (e.g. URL_CALLING_SERVICE
    // unset or TURN fetch failed), iceTransportPolicy:'relay' would suppress
    // host/srflx and leave the browser with zero usable candidates — every
    // call would fail. Better to log the request and silently downgrade than
    // turn the kill switch into a footgun during a TURN outage.
    const forceRelayRequested = isForcedRelay();
    const hasTurnForRelay = iceServers.some(s => s.username && s.credential);
    const forceRelay = forceRelayRequested && hasTurnForRelay;
    if (forceRelayRequested && !hasTurnForRelay) {
        logger.warn('Force-relay requested but no TURN servers available — falling back to non-relay', {
            source: 'sip', event: 'force_relay_no_turn',
            direction: 'outgoing',
        });
    }
    const callOptions = {
        ...options,
        pcConfig: {
            ...options.pcConfig,
            iceServers: iceServers,
            gatheringTimeout: getIceGatheringTimeout(),
            ...(forceRelay ? {iceTransportPolicy: 'relay' as const} : {}),
        },
        rtcOfferConstraints: {
            offerToReceiveAudio: true,
            offerToReceiveVideo: false
        },
        // SDP mangling (Opus DTX/FMTP + rtcp-fb:nack + maxptime:20 + mDNS
        // strip) is wired up via installSdpMangling() on the `sdp` event
        // inside the `newRTCSession` handler — JsSIP 3.x does not honour
        // `sdpTransform` in call options.
    };

    session = sipUserAgent.call(`sip:${phoneNumber}@${tenant}`, callOptions);
    _callInProgress = false;

    // Setup smart ICE candidate handling for outgoing calls
    if (session) {
        const hasTurn = iceServers.some(s => s.username && s.credential);
        setupICECandidateHandling(session, getCallTimings(session), hasTurn);
    }

    if (session) {
        const callId: string = getCallId(session);
        logger.info('Outgoing call initiated', {
            source: 'sip',
            event: 'call_initiated',
            direction: 'outgoing',
            callId,
            phoneNumber,
            hasTurn: iceServers.some(s => s.username && s.credential),
            forceRelay,
        });
        await setupAudioStream(session, audioDeviceId);
    }
};

const answerCall = async (dispatch: any, options: any, audioDeviceId: string) => {
    dispatch = tagEngine('sip', dispatch);
    if (session) {
        // Check if session is already answered/confirmed
        // JsSIP status codes: 0=NULL, 1=INVITE_SENT, 2=1XX_RECEIVED, 3=INVITE_RECEIVED,
        // 4=WAITING_FOR_ANSWER, 5=ANSWERED, 6=WAITING_FOR_ACK, 7=CANCELED, 8=TERMINATED, 9=CONFIRMED
        const sessionStatus = (session as any)._status;
        if (sessionStatus >= 5) { // ANSWERED or later
            logger.warn('Duplicate answer attempt ignored', {
                source: 'sip',
                event: 'answer_duplicate_ignored',
                callId: (session as any)?.id,
                sessionStatus,
            });
            return;
        }

        const answerFlowStartAt = Date.now();
        getCallTimings(session).answerFlowStartAt = answerFlowStartAt;

        try {
            // Force-refresh TURN credentials for incoming calls — cached credentials
            // may be stale or exhausted (Cloudflare limits concurrent allocations per
            // credential). Without this, incoming calls sometimes fall back to
            // host+srflx only, causing suboptimal media paths.
            if (getAudioFlags().clearIceCacheBeforeAnswer) {
                clearIceServerCache();
            }
            const iceServers = await getIceServers();

            // BBB pattern #2 — see outbound path for rationale + safety gate.
            const forceRelayRequested = isForcedRelay();
            const hasTurnForRelay = iceServers.some(s => s.username && s.credential);
            const forceRelay = forceRelayRequested && hasTurnForRelay;
            if (forceRelayRequested && !hasTurnForRelay) {
                logger.warn('Force-relay requested but no TURN servers available — falling back to non-relay', {
                    source: 'sip', event: 'force_relay_no_turn',
                    direction: 'incoming',
                });
            }
            const answerOptions = {
                ...options,
                pcConfig: {
                    ...options.pcConfig,
                    iceServers: iceServers,
                    gatheringTimeout: getIceGatheringTimeout(),
                    ...(forceRelay ? {iceTransportPolicy: 'relay' as const} : {}),
                },
                // SDP mangling hooked via installSdpMangling() on the session's
                // `sdp` event in the newRTCSession handler — the `sdpTransform`
                // option is not consumed by JsSIP 3.x.
            };
            logger.info('Answering incoming call', {
                source: 'sip',
                event: 'call_answer',
                direction: 'incoming',
                callId: (session as any)?.id,
                forceRelay,
            });
            session.answer(answerOptions);
            const answerSentMs = Date.now() - answerFlowStartAt;
            getCallTimings(session).answerSentMs = answerSentMs;
            logger.info('SIP answer sent', {
                source: 'sip',
                event: 'answer_sent',
                direction: 'incoming',
                callId: (session as any)?.id,
                answerSentMs,
            });
            await setupAudioStream(session, audioDeviceId);

            dispatch(setCallState(CallStateEnum.IN_CALL));
            dispatch(setIncomingCall(false));
        } catch (error) {
            logger.error('Error answering call', {
                source: 'sip',
                event: 'call_answer_error',
                callId: (session as any)?.id,
                errorMessage: (error as Error)?.message,
            });
            // Don't rethrow - already answered calls will error but session is still valid
        }
    }
};
const setVolume = (volume: number) => {
    if (volume < 0 || volume > 1) {
        logger.warn('Invalid volume value', { source: 'sip', event: 'volume_invalid', volume });
        return;
    }

    if (audioRef) {
        audioRef.volume = volume;
        logger.debug('Call audio volume changed', { source: 'sip', event: 'volume_set', volume });
    }
};
/**
 * Best-effort force close of the PeerConnection after session.terminate().
 * Covers the rare case where terminate() threw or the PC was left in a
 * non-closed state (e.g. JsSIP internal error), which would otherwise leak
 * media tracks and keep the mic/codec busy for the next call.
 */
const forceClosePeerConnection = (s: RTCSession | null): void => {
    if (!s) return;
    const pc = s.connection;
    if (!pc) return;
    if (pc.signalingState === 'closed') return;
    try {
        pc.close();
        logger.info('PeerConnection force-closed after terminate', {
            source: 'sip',
            event: 'pc_force_closed_after_terminate',
            callId: (s as any)?.id,
        });
    } catch (error) {
        logger.warn('Failed to force-close PeerConnection', {
            source: 'sip',
            event: 'pc_force_close_failed',
            callId: (s as any)?.id,
            errorMessage: (error as Error)?.message,
        });
    }
};

const rejectCall = (dispatch: any) => {
    dispatch = tagEngine('sip', dispatch);
    // Abort any in-progress call() that is still awaiting TURN fetch
    _callAborted = true;
    markSessionEnded(session);
    if (session) {
        try {
            session.terminate({
                status_code: 603,
                reason_phrase: 'Decline',
            });
        } finally {
            forceClosePeerConnection(session);
        }
    }
    dispatch(setIncomingCall(false));
    dispatch(setCallFrom(null));
};

const endCall = (dispatch: any) => {
    dispatch = tagEngine('sip', dispatch);
    // Abort any in-progress call() that is still awaiting TURN fetch.
    // Without this, if the user hangs up while getIceServers() is pending,
    // sipUserAgent.call() fires a ghost INVITE after the UI cleaned up.
    _callAborted = true;
    markSessionEnded(session);
    if (session) {
        dispatch(setCallFrom(null));
        try {
            session.terminate();
        } finally {
            forceClosePeerConnection(session);
        }
    }
};
const stopSipForBlockedAccount = async (dispatch: AppDispatch): Promise<void> => {
    dispatch = tagEngine('sip', dispatch);
    stopSipAuthRecovery();
    try {
        endCall(dispatch);
    } catch (error) {
        logger.warn('Failed to end SIP call for blocked account', {
            source: 'sip',
            event: 'sip_blocked_end_call_failed',
            error: error instanceof Error ? error.message : String(error),
        });
    }
    ringingAudioRef.pause();
    RingbackManager.getInstance().stop();
    dispatch(stopRingback());
    dispatch(stopRingtone());
    dispatch(setIncomingCall(false));
    if (sipUserAgent) {
        try {
            await sipUserAgent.stop();
        } catch (error) {
            logger.warn('Failed to stop SIP UA for blocked account', {
                source: 'sip',
                event: 'sip_blocked_stop_failed',
                error: error instanceof Error ? error.message : String(error),
            });
        }
    }
};
const toggleHold = () => {
    if(!session) return;
    if (session.isOnHold().local) {
        session.unhold();
    } else {
        session.hold();
    }
    publishSipControls();
};

const toggleMuted = () => {
    if(!session) return;
    if (session.isMuted().audio) {
        session.unmute();
    } else {
        session.mute();
    }
    publishSipControls();
};

export const setSipOutputDevice = (deviceId: string): Promise<void> =>
    setOutputDevice(audioRef, deviceId, session ? getCallId(session) : undefined);

export const switchSipMicrophone = async (deviceId: string, autoGainControl: boolean): Promise<void> => {
    const sender = session?.connection?.getSenders().find(item => item.track?.kind === 'audio');
    if (!session || !sender) return;
    const callId = getCallId(session);
    try {
        const audio = {...buildAudioConstraints(deviceId, autoGainControl), ...(deviceId ? {deviceId: {exact: deviceId}} : {})};
        const stream = await navigator.mediaDevices.getUserMedia({audio});
        const [track] = stream.getAudioTracks();
        if (!track) return;
        track.enabled = !session.isMuted().audio;
        const previous = sender.track;
        await sender.replaceTrack(track);
        previous?.stop();
        logger.info('Microphone switched mid-call', {source: 'sip', event: 'microphone_switched', callId, deviceId});
    } catch (error) {
        logger.error('Failed to switch microphone mid-call', {
            source: 'sip', event: 'microphone_switch_error',
            callId, deviceId,
            errorMessage: (error as Error)?.message,
        });
    }
};

const sendDtmf = (digit: string, opts?: { duration?: number; gap?: number }) => {
    if (!session) return;

    const duration = opts?.duration ?? DEFAULT_DTMF_DURATION;
    const interToneGap = opts?.gap ?? DEFAULT_DTMF_INTER_TONE_GAP;

    try {
        // Prefer RTP telephone-event (RFC2833 / RFC4733)
        session.sendDTMF(digit, {
            duration,
            interToneGap,
            transportType: DTMF_TRANSPORT.RFC2833
        });
    } catch (err) {
        // Fallback to SIP INFO (application/dtmf-relay) if RTP DTMF unsupported
        logger.warn('RFC2833 DTMF failed, falling back to SIP INFO', {
            source: 'sip',
            event: 'dtmf_rfc2833_fallback',
            digit,
            errorMessage: (err as Error)?.message,
        });
        session.sendDTMF(digit, {
            duration,
            interToneGap,
            transportType: DTMF_TRANSPORT.INFO
        });
    }
};



export {
    initializeSip,
    call,
    answerCall,
    rejectCall,
    endCall,
    stopSipForBlockedAccount,
    setVolume,
    toggleHold,
    toggleMuted,
    sendDtmf,
    createDefaultEventHandlers
};
